import { AnyAction } from 'redux';
import { FamilienStand, Selbstbeteiligung } from '@ps20/bff-models';
import {
  FamilienstandChanged,
  GeburtsdatumChanged,
  HausnummerChanged,
  OrtChanged,
  PhVorbelegungdaten,
  PlzChanged,
  Produktlinie,
  StrasseChanged,
} from '@ps20-frontend/frontend-models';
import { TARIF_CHECKED } from '@ps20-frontend/shop-integration-redux';
import { Injectable } from '@angular/core';
import { AntragsummerGenerated } from './action-types/antragsummerGenerated';

export interface PhAction<T = undefined> extends AnyAction {
  payload?: T;
}

@Injectable()
export class PhActions {
  static readonly ABSPRUNG_INS_DASHBOARD = 'ABSPRUNG_INS_DASHBOARD';
  static readonly ANTRAGSNUMMER_GENERATED = 'ANTRAGSNUMMER_GENERATED';
  static readonly ANZAHL_WOHNUNGEN_CHANGED = 'ANZAHL_WOHNUNGEN_CHANGED';
  static readonly BRUTTOPREISE_CALCULATED = 'BRUTTOPREISE_CALCULATED';
  static readonly FAMILIENSTAND_CHANGED = 'FAMILIENSTAND_CHANGED';
  static readonly GEBURTSDATUM_CHANGED = 'GEBURTSDATUM_CHANGED';
  static readonly HAUSNUMMER_CHANGED = 'HAUSNUMMER_CHANGED';
  static readonly ORT_CHANGED = 'ORT_CHANGED';
  static readonly PLZ_CHANGED = 'PLZ_CHANGED';
  static readonly PRODUKTLINIEN_CHANGED = 'PRODUKTLINIEN_CHANGED';
  static readonly PRODUKT_DATEN_VORBELEGT = 'PRODUKT_DATEN_VORBELEGT';
  static readonly PREISE_REQUESTED = 'PREISE_REQUESTED';
  static readonly STRASSE_CHANGED = 'STRASSE_CHANGED';
  static readonly VERSICHERUNGSSUMME_CHANGED = 'VERSICHERUNGSSUMME_CHANGED';
  static readonly VORSCHAEDEN_VORHANDEN = 'VORSCHAEDEN_VORHANDEN';
  static readonly VORSCHADEN_VALUE_CHANGED = 'VORSCHADEN_VALUE_CHANGED';
  static readonly ZUM_ANGEBOT_BUTTON_CLICKED = 'ZUM_ANGEBOT_BUTTON_CLICKED';
  static readonly ZURUECK_ZUM_SHOP_MODAL_CLOSE = 'ZURUECK_ZUM_SHOP_MODAL_CLOSE';
  static readonly ZURUECK_ZUM_SHOP_MODAL_OPEN = 'ZURUECK_ZUM_SHOP_MODAL_OPEN';
  static readonly SELBSTBETEILIGUNG_CHANGED = 'SELBSTBETEILIGUNG_CHANGED';
  static readonly ZUSATZOPTION_CHECKED = 'ZUSATZOPTION_CHECKED';
  static readonly ZUSATZOPTION_UNCHECKED = 'ZUSATZOPTION_UNCHECKED';

  static readonly VORSCHLAGDATEN_CHANGED = 'VORSCHLAGDATEN_CHANGED';
  static readonly QUOTEDB_REQUESTED_PH = 'QUOTEDB_REQUESTED_PH';
  static readonly HANDLE_PH_NAVIGATION_TO_ANGEBOT = 'HANDLE_PH_NAVIGATION_TO_ANGEBOT';

  antragsnummerGenerated(antragsnummer: string): PhAction<AntragsummerGenerated> {
    return {
      type: PhActions.ANTRAGSNUMMER_GENERATED,
      payload: { antragsnummer },
    };
  }

  absprungInsDashboard(): PhAction<undefined> {
    return { type: PhActions.ABSPRUNG_INS_DASHBOARD };
  }

  anzahlWohnungenChanged(value: number): PhAction<number> {
    return { type: PhActions.ANZAHL_WOHNUNGEN_CHANGED, payload: value };
  }

  bruttopreiseCalculated(
    produktlinienMitBruttopreisen: Array<Produktlinie>,
  ): PhAction<Array<Produktlinie>> {
    return {
      type: PhActions.BRUTTOPREISE_CALCULATED,
      payload: produktlinienMitBruttopreisen,
    };
  }

  familienstandChanged(familienstand: FamilienStand): PhAction<FamilienstandChanged> {
    return {
      type: PhActions.FAMILIENSTAND_CHANGED,
      payload: { familienstand },
    };
  }

  geburtsdatumChanged(geburtsdatum: string): PhAction<GeburtsdatumChanged> {
    return { type: PhActions.GEBURTSDATUM_CHANGED, payload: { geburtsdatum } };
  }

  hausnummerChanged(hausnummer: string): PhAction<HausnummerChanged> {
    return { type: PhActions.HAUSNUMMER_CHANGED, payload: { hausnummer } };
  }

  ortChanged(ort: string): PhAction<OrtChanged> {
    return { type: PhActions.ORT_CHANGED, payload: { ort } };
  }

  plzChanged(plz: string): PhAction<PlzChanged> {
    return { type: PhActions.PLZ_CHANGED, payload: { plz } };
  }

  preiseRequested(): PhAction {
    return { type: PhActions.PREISE_REQUESTED };
  }

  produktlinienChanged(produktlinien: Produktlinie[]): PhAction<Produktlinie[]> {
    return { type: PhActions.PRODUKTLINIEN_CHANGED, payload: produktlinien };
  }

  produktDatenVorbelegt(vorbelegundaten: PhVorbelegungdaten): PhAction<PhVorbelegungdaten> {
    return {
      type: PhActions.PRODUKT_DATEN_VORBELEGT,
      payload: vorbelegundaten,
    };
  }

  vorschaedenVorhandenChanged(vorhanden: boolean): PhAction<boolean> {
    return { type: PhActions.VORSCHAEDEN_VORHANDEN, payload: vorhanden };
  }

  vorschadenValueChanged(
    vorschadenId: string,
    value: number,
  ): PhAction<{ vorschadenId: string; value: number }> {
    return {
      type: PhActions.VORSCHADEN_VALUE_CHANGED,
      payload: { vorschadenId, value },
    };
  }

  strasseChanged(strasse: string): PhAction<StrasseChanged> {
    return { type: PhActions.STRASSE_CHANGED, payload: { strasse } };
  }

  zumAngebotButtonClicked(): PhAction {
    return { type: PhActions.ZUM_ANGEBOT_BUTTON_CLICKED };
  }

  zurueckZumShopModalClose(): PhAction {
    return { type: PhActions.ZURUECK_ZUM_SHOP_MODAL_CLOSE };
  }

  zurueckZumShopModalOpen(): PhAction {
    return { type: PhActions.ZURUECK_ZUM_SHOP_MODAL_OPEN };
  }

  tarifChecked(index: number): PhAction<{ index: number }> {
    return { type: TARIF_CHECKED, payload: { index } };
  }

  selbstbeteiligungChanged(selbstbeteiligung: Selbstbeteiligung): PhAction<Selbstbeteiligung> {
    return {
      type: PhActions.SELBSTBETEILIGUNG_CHANGED,
      payload: selbstbeteiligung,
    };
  }

  zusatzoptionChecked(name: string): PhAction<{ name: string }> {
    return { type: PhActions.ZUSATZOPTION_CHECKED, payload: { name } };
  }

  zusatzoptionUnchecked(name: string): PhAction<{ name: string }> {
    return { type: PhActions.ZUSATZOPTION_UNCHECKED, payload: { name } };
  }

  versicherungsSummeChanged(versSummeLeistung: number): PhAction<{ versSummeLeistung: number }> {
    return {
      type: PhActions.VERSICHERUNGSSUMME_CHANGED,
      payload: { versSummeLeistung },
    };
  }

  vorschlagdatenChanged(): AnyAction {
    return { type: PhActions.VORSCHLAGDATEN_CHANGED };
  }

  quoteDBRequested(): AnyAction {
    return { type: PhActions.QUOTEDB_REQUESTED_PH };
  }

  handlePhNavigationToAngebot(): PhAction {
    return { type: PhActions.HANDLE_PH_NAVIGATION_TO_ANGEBOT };
  }
}
